<template>
  <div class="timeline-container bg-white border-t shadow-lg fixed bottom-0 left-0 right-0 z-40">
    <div class="max-w-7xl mx-auto px-2 py-1.5">
      <div class="flex items-center space-x-2 flex-wrap gap-1">
        <!-- Playback Controls -->
        <div class="flex items-center space-x-0.5 bg-gray-100 rounded-md p-0.5">
          <button @click="jumpToStart" class="p-1 rounded hover:bg-white transition-colors" title="Go to start">
            <i class="fas fa-fast-backward text-xs"></i>
          </button>
          <button @click="stepBackward" class="p-1 rounded hover:bg-white transition-colors" title="Step backward 5 min">
            <i class="fas fa-step-backward text-xs"></i>
          </button>
          <button 
            @click="togglePlayback" 
            class="p-1 rounded hover:bg-white transition-colors w-6 flex justify-center" 
            :class="{ 'text-blue-600': isPlaying }"
          >
            <i v-if="isPlaying" class="fas fa-pause text-xs"></i>
            <i v-else-if="isAtEnd" class="fas fa-redo-alt text-xs"></i>
            <i v-else class="fas fa-play text-xs"></i>
          </button>
          <button @click="stepForward" class="p-1 rounded hover:bg-white transition-colors" title="Step forward 5 min">
            <i class="fas fa-step-forward text-xs"></i>
          </button>
          <button @click="jumpToEnd" class="p-1 rounded hover:bg-white transition-colors" title="Go to end">
            <i class="fas fa-fast-forward text-xs"></i>
          </button>
          <button @click="stopPlayback" class="p-1 rounded hover:bg-white transition-colors" title="Stop">
            <i class="fas fa-stop text-xs"></i>
          </button>
        </div>
        
        <!-- Speed Control -->
        <select v-model="playbackSpeed" class="px-1.5 py-0.5 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white">
          <option value="0.25">0.25x</option>
          <option value="0.5">0.5x</option>
          <option value="1">1x</option>
          <option value="2">2x</option>
          <option value="5">5x</option>
          <option value="10">10x</option>
          <option value="30">30x</option>
          <option value="60">60x</option>
        </select>
        
        <!-- Date/Time Display -->
        <div class="flex items-center space-x-1 text-[10px] bg-gray-100 px-1.5 py-0.5 rounded-md">
          <i class="fas fa-calendar-alt text-gray-500 text-[9px]"></i>
          <span class="font-mono">{{ formatDateTime(currentTime) }}</span>
        </div>
        
        <!-- Tracked Ship Info -->
        <div v-if="trackedShip" class="flex items-center space-x-1 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-md">
          <i class="fas fa-ship text-[9px]"></i>
          <span class="font-medium truncate max-w-[120px]">{{ trackedShip.name || trackedShip.mmsi }}</span>
          <button @click="centerOnTrackedShip" class="ml-0.5 text-green-600 hover:text-green-800" title="Center on ship">
            <i class="fas fa-crosshairs text-[9px]"></i>
          </button>
        </div>
        
        <!-- End Status Indicator with Replay Button -->
        <div v-if="isAtEnd" class="flex items-center space-x-1 text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-md">
          <i class="fas fa-flag-checkered text-[9px]"></i>
          <span>Complete</span>
          <button 
            @click="replay" 
            class="ml-0.5 px-1.5 py-0.5 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors text-[9px]"
            title="Replay from start"
          >
            <i class="fas fa-redo-alt mr-0.5 text-[8px]"></i> Replay
          </button>
        </div>
        
        <!-- Time Range Slider -->
        <div class="flex-1 min-w-[150px]">
          <div class="flex items-center justify-between text-[8px] text-gray-500 mb-0.5">
            <span>{{ formatTime(timeRange.start) }}</span>
            <span class="font-mono">{{ formatDuration() }}</span>
            <span>{{ formatTime(timeRange.end) }}</span>
          </div>
          <div class="relative">
            <input 
              v-model="currentProgress" 
              type="range" 
              min="0" 
              max="100" 
              step="0.1" 
              class="timeline-slider w-full" 
              @input="handleSeek"
              :class="{ 'at-end': isAtEnd }"
            />
          </div>
        </div>
        
        <!-- Stats -->
        <div class="flex items-center space-x-0.5 px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-md">
          <i class="fas fa-chart-line text-[9px]"></i>
          <span class="text-[10px] font-medium">{{ totalTrackPoints }}</span>
        </div>
        
        <!-- Close Button -->
        <button @click="$emit('close')" class="p-1 hover:bg-gray-100 rounded-md transition-colors" title="Close timeline">
          <i class="fas fa-times text-xs"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted, onMounted } from 'vue'
import { format } from 'date-fns'

const props = defineProps({
  enabled: { type: Boolean, default: false },
  initialDate: { type: String, default: () => format(new Date(), 'yyyy-MM-dd') },
  initialShip: { type: Object, default: null },
  trackHistory: { type: Array, default: () => [] }
})

const emit = defineEmits(['time-change', 'snapshot-load', 'close', 'center-ship', 'replay', 'playback-state'])

// State
const isPlaying = ref(false)
const playbackSpeed = ref(1)
const currentTime = ref(new Date())
const currentProgress = ref(0)
const timeRange = ref({
  start: new Date(),
  end: new Date()
})
const trackedShip = ref(props.initialShip)
const isAtEnd = ref(false)
const totalTrackPoints = ref(0)

let playbackInterval = null

// Computed
const isAtStart = computed(() => {
  return currentTime.value.getTime() <= timeRange.value.start.getTime()
})

// Watch trackHistory prop
watch(() => props.trackHistory, (history) => {
  if (history && history.length > 0) {
    totalTrackPoints.value = history.length
    
    const startTime = new Date(history[0].ts)
    const endTime = new Date(history[history.length - 1].ts)
    timeRange.value = { start: startTime, end: endTime }
    currentTime.value = new Date(startTime)
    isAtEnd.value = false
    updateProgress()
    
  } else {
    totalTrackPoints.value = 0
  }
}, { deep: true, immediate: true })

// Watch isPlaying for playback
watch(isPlaying, (playing) => {
  if (playing) {
    if (isAtEnd.value) {
      replay()
    } else {
      startPlayback()
    }
  } else {
    stopPlayback()
  }
})

watch([() => timeRange.value.start, () => timeRange.value.end], () => updateProgress())

watch(() => props.initialShip, (ship) => {
  if (ship) trackedShip.value = ship
})

// ========== PLAYBACK CONTROL FUNCTIONS ==========

function setTimeRange(start, end) {
  timeRange.value = { start: new Date(start), end: new Date(end) }
  currentTime.value = new Date(start)
  isAtEnd.value = false
  updateProgress()
}

function startPlayback() {
  if (playbackInterval) clearInterval(playbackInterval)
  
  if (isAtEnd.value) {
    return
  }
  
  // Emit bahwa playback mulai
  emit('playback-state', true)
  
  playbackInterval = setInterval(() => {
    const newTime = new Date(currentTime.value.getTime() + 1000 * playbackSpeed.value)
    
    if (newTime >= timeRange.value.end) {
      currentTime.value = new Date(timeRange.value.end)
      updateProgress()
      emit('time-change', currentTime.value)
      stopPlayback()
      isPlaying.value = false
      isAtEnd.value = true
      // Emit bahwa playback berhenti
      emit('playback-state', false)
    } else {
      currentTime.value = newTime
      updateProgress()
      emit('time-change', currentTime.value)
    }
  }, 50)
}

function stopPlayback() {
  if (playbackInterval) {
    clearInterval(playbackInterval)
    playbackInterval = null
  }
  isPlaying.value = false
  // Emit bahwa playback berhenti
  emit('playback-state', false)
}

function togglePlayback() {
  if (isPlaying.value) {
    isPlaying.value = false
  } else if (isAtEnd.value) {
    replay()
  } else {
    isPlaying.value = true
  }
}

function replay() {
  console.log('🔄 Replay from start')
  
  stopPlayback()
  
  currentTime.value = new Date(timeRange.value.start)
  isAtEnd.value = false
  updateProgress()
  
  emit('time-change', currentTime.value)
  emit('replay')
  
  // Emit bahwa replay dimulai (playback mulai)
  emit('playback-state', true)
  
  isPlaying.value = true
  startPlayback()
}

function stepBackward() {
  // Hentikan playback saat step
  const wasPlaying = isPlaying.value
  if (wasPlaying) {
    stopPlayback()
    emit('playback-state', false)
  }
  
  const newTime = new Date(currentTime.value.getTime() - 5 * 60 * 1000)
  if (newTime >= timeRange.value.start) {
    currentTime.value = newTime
    isAtEnd.value = false
    updateProgress()
    emit('time-change', newTime)
  } else {
    currentTime.value = new Date(timeRange.value.start)
    isAtEnd.value = false
    updateProgress()
    emit('time-change', currentTime.value)
  }
}

function stepForward() {
  // Hentikan playback saat step
  const wasPlaying = isPlaying.value
  if (wasPlaying) {
    stopPlayback()
    emit('playback-state', false)
  }
  
  const newTime = new Date(currentTime.value.getTime() + 5 * 60 * 1000)
  if (newTime <= timeRange.value.end) {
    currentTime.value = newTime
    isAtEnd.value = newTime.getTime() === timeRange.value.end.getTime()
    updateProgress()
    emit('time-change', newTime)
  } else {
    currentTime.value = new Date(timeRange.value.end)
    isAtEnd.value = true
    updateProgress()
    emit('time-change', currentTime.value)
  }
}

function jumpToStart() {
  const wasPlaying = isPlaying.value
  if (wasPlaying) {
    stopPlayback()
    emit('playback-state', false)
  }
  currentTime.value = new Date(timeRange.value.start)
  isAtEnd.value = false
  updateProgress()
  emit('time-change', currentTime.value)
}

function jumpToEnd() {
  const wasPlaying = isPlaying.value
  if (wasPlaying) {
    stopPlayback()
    emit('playback-state', false)
  }
  currentTime.value = new Date(timeRange.value.end)
  isAtEnd.value = true
  updateProgress()
  emit('time-change', currentTime.value)
}

function handleSeek() {
  const wasPlaying = isPlaying.value
  if (wasPlaying) {
    stopPlayback()
    emit('playback-state', false)
  }
  const range = timeRange.value.end.getTime() - timeRange.value.start.getTime()
  const newTime = new Date(timeRange.value.start.getTime() + (range * currentProgress.value / 100))
  currentTime.value = newTime
  isAtEnd.value = newTime.getTime() === timeRange.value.end.getTime()
  emit('time-change', newTime)
}

function updateProgress() {
  const range = timeRange.value.end.getTime() - timeRange.value.start.getTime()
  const elapsed = currentTime.value.getTime() - timeRange.value.start.getTime()
  currentProgress.value = (elapsed / range) * 100
}

function centerOnTrackedShip() {
  if (trackedShip.value && trackedShip.value.lat && trackedShip.value.lng) {
    emit('center-ship', trackedShip.value)
  }
}

// ========== UTILITY FUNCTIONS ==========

function formatDateTime(date) {
  return format(date, 'dd MMM HH:mm:ss')
}

function formatTime(date) {
  return format(date, 'HH:mm')
}

function formatDuration() {
  const start = timeRange.value.start
  const end = timeRange.value.end
  const diffMs = end.getTime() - start.getTime()
  const hours = Math.floor(diffMs / (60 * 60 * 1000))
  const minutes = Math.floor((diffMs % (60 * 60 * 1000)) / (60 * 1000))
  return `${hours}h ${minutes}m`
}

// ========== EXPOSE METHODS TO PARENT ==========

defineExpose({
  setTimeRange,
  stopPlayback,
  startPlayback,
  replay,
  jumpToStart,
  jumpToEnd,
  isPlaying,
  isAtEnd
})

// ========== LIFECYCLE ==========

onMounted(() => {
  console.log('🎬 TimelinePlayer mounted')
})

onUnmounted(() => {
  stopPlayback()
})
</script>

<style scoped>
.timeline-container { 
  pointer-events: auto; 
}

.timeline-slider { 
  -webkit-appearance: none; 
  appearance: none; 
  height: 4px; 
  background: #e5e7eb; 
  border-radius: 2px; 
  outline: none; 
}

.timeline-slider::-webkit-slider-thumb { 
  -webkit-appearance: none; 
  appearance: none; 
  width: 12px; 
  height: 12px; 
  background: #3b82f6; 
  border-radius: 50%; 
  cursor: pointer; 
  box-shadow: 0 1px 2px rgba(0,0,0,0.2); 
}

.timeline-slider::-webkit-slider-thumb:hover { 
  transform: scale(1.1); 
}

.timeline-slider.at-end::-webkit-slider-thumb {
  background: #f59e0b;
}

button:active {
  transform: scale(0.95);
}

button:hover .fa-redo-alt {
  animation: spin 0.5s ease;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>